import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Lock, ShieldCheck } from 'lucide-react';
import { supabase } from '@/services/supabase';

type PageState = 'loading' | 'confirm' | 'error' | 'form' | 'done';

function decodeBase64Url(encoded: string): string {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
  return atob(padded);
}

const BG_STYLE = {
  background: 'linear-gradient(180deg, #010816 0%, #02112B 50%, #04193D 100%)',
} as const;

const CARD_STYLE = {
  background: 'rgba(10, 20, 45, 0.55)',
  border: '1px solid rgba(184, 197, 224, 0.18)',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
} as const;

export function ResetPasswordPage() {
  const [state, setState] = useState<PageState>('loading');
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // ── Case 1: email link with encoded Supabase URL (?s=base64url) ──────────
    // The email points to our page with the Supabase verify URL encoded as a
    // query param so email scanners cannot pre-fetch and consume the OTP.
    const searchParams = new URLSearchParams(window.location.search);
    const encoded = searchParams.get('s');
    if (encoded) {
      try {
        const decoded = decodeBase64Url(encoded);
        // Basic sanity check — must be a Supabase auth URL
        if (decoded.includes('/auth/v1/verify') || decoded.includes('supabase.co')) {
          setSupabaseUrl(decoded);
          setState('confirm');
          return;
        }
      } catch {
        // fall through to error
      }
      setErrorMsg('El enlace del correo es inválido. Solicita uno nuevo desde la aplicación.');
      setState('error');
      return;
    }

    // ── Case 2: Supabase redirected back with error in hash ──────────────────
    // Check for error params in the URL hash (e.g. otp_expired)
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : '';
    const params = new URLSearchParams(hash);
    const error = params.get('error');
    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');

    if (error) {
      const msg =
        errorCode === 'otp_expired'
          ? 'El enlace de restablecimiento ha expirado (duración máxima: 1 hora). Solicita un nuevo enlace desde la aplicación.'
          : errorDescription
          ? decodeURIComponent(errorDescription).replace(/\+/g, ' ')
          : 'El enlace es inválido o ya fue usado. Solicita uno nuevo desde la aplicación.';
      setErrorMsg(msg);
      setState('error');
      return;
    }

    // Listen for PASSWORD_RECOVERY auth event — Supabase automatically
    // extracts the tokens from the URL hash when detectSessionInUrl=true.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setState('form');
      }
    });

    // Fallback: if no event fires in 5 seconds, the link was likely invalid
    timeoutRef.current = setTimeout(() => {
      setState((prev) => {
        if (prev === 'loading') {
          setErrorMsg('No se pudo verificar el enlace. Es posible que haya expirado o ya fue usado.');
          return 'error';
        }
        return prev;
      });
    }, 5000);

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  async function handleConfirm() {
    setVerifying(true);
    try {
      const url = new URL(supabaseUrl);
      const tokenHash = url.searchParams.get('token');
      if (!tokenHash) throw new Error('token ausente en el enlace');
      // Verify the OTP in-place — no page navigation needed.
      // This establishes the session and fires PASSWORD_RECOVERY.
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery',
      });
      if (error) throw error;
      setState('form');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      const isExpired =
        msg.toLowerCase().includes('expired') ||
        msg.toLowerCase().includes('invalid') ||
        msg.toLowerCase().includes('otp');
      setErrorMsg(
        isExpired
          ? 'El enlace ha expirado o ya fue usado. Solicita uno nuevo desde la aplicación.'
          : `No se pudo verificar el enlace: ${msg}`,
      );
      setState('error');
    } finally {
      setVerifying(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (password.length < 8) {
      setFormError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setFormError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setFormError(error.message);
        return;
      }
      // Update succeeded — advance UI immediately, signOut in background
      setState('done');
      supabase.auth.signOut().catch(() => {});
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al actualizar la contraseña';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={BG_STYLE}>
      <div className="w-full max-w-md rounded-2xl p-8 text-center" style={CARD_STYLE}>
        <img src="/logo_dark.png" alt="ASI Security" className="mx-auto mb-6 h-16 w-auto" />

        {/* ── Confirm (scanner-safe: user must click to proceed) ──────── */}
        {state === 'confirm' && (
          <>
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ background: 'rgba(245, 158, 11, 0.15)', border: '2px solid #F59E0B' }}
            >
              <ShieldCheck size={44} color="#F59E0B" strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>
              Restablecer contraseña
            </h1>
            <p className="text-base mb-8" style={{ color: '#B8C5E0' }}>
              Recibimos tu solicitud. Haz clic en el botón para continuar.
            </p>
            <button
              onClick={handleConfirm}
              disabled={verifying}
              className="w-full rounded-lg py-3 text-sm font-semibold transition-opacity disabled:opacity-60"
              style={{ background: '#F59E0B', color: '#000000' }}
            >
              {verifying ? 'Verificando…' : 'Confirmar restablecimiento'}
            </button>
            <p className="mt-4 text-xs" style={{ color: '#7A8AA8' }}>
              Este enlace es de un solo uso y expira en 1 hora.
            </p>
          </>
        )}

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {state === 'loading' && (
          <>
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ background: 'rgba(10, 132, 255, 0.15)', border: '2px solid #0A84FF' }}
            >
              <Lock size={44} color="#0A84FF" strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>
              Verificando enlace…
            </h1>
            <p className="text-base" style={{ color: '#B8C5E0' }}>
              Por favor espera un momento.
            </p>
          </>
        )}

        {/* ── Error ───────────────────────────────────────────────────── */}
        {state === 'error' && (
          <>
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ background: 'rgba(239, 68, 68, 0.15)', border: '2px solid #EF4444' }}
            >
              <AlertTriangle size={44} color="#EF4444" strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>
              Enlace inválido o expirado
            </h1>
            <p className="text-base mb-6" style={{ color: '#B8C5E0' }}>
              {errorMsg}
            </p>
            <div
              className="flex items-start gap-3 rounded-xl p-4 text-left"
              style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
              }}
            >
              <Lock size={20} color="#EF4444" className="mt-0.5 shrink-0" />
              <p className="text-sm" style={{ color: '#B8C5E0' }}>
                Abre la aplicación <strong>ASI Security</strong> o{' '}
                <strong>ASI Patrol</strong> y solicita un nuevo correo de
                restablecimiento desde la pantalla de inicio de sesión.
              </p>
            </div>
          </>
        )}

        {/* ── Form ────────────────────────────────────────────────────── */}
        {state === 'form' && (
          <>
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ background: 'rgba(10, 132, 255, 0.15)', border: '2px solid #0A84FF' }}
            >
              <Lock size={44} color="#0A84FF" strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>
              Nueva contraseña
            </h1>
            <p className="text-base mb-6" style={{ color: '#B8C5E0' }}>
              Elige una contraseña segura para tu cuenta.
            </p>

            <form onSubmit={handleSubmit} className="text-left space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#B8C5E0' }}>
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg px-4 py-2.5 pr-10 text-sm outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(184, 197, 224, 0.25)',
                      color: '#FFFFFF',
                    }}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff size={16} color="#B8C5E0" />
                    ) : (
                      <Eye size={16} color="#B8C5E0" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#B8C5E0' }}>
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full rounded-lg px-4 py-2.5 pr-10 text-sm outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(184, 197, 224, 0.25)',
                      color: '#FFFFFF',
                    }}
                    placeholder="Repite la contraseña"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                    tabIndex={-1}
                  >
                    {showConfirm ? (
                      <EyeOff size={16} color="#B8C5E0" />
                    ) : (
                      <Eye size={16} color="#B8C5E0" />
                    )}
                  </button>
                </div>
              </div>

              {formError && (
                <p className="text-sm text-red-400">{formError}</p>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full rounded-lg py-2.5 text-sm font-semibold transition-opacity disabled:opacity-60"
                style={{ background: '#0A84FF', color: '#FFFFFF' }}
              >
                {saving ? 'Guardando…' : 'Actualizar contraseña'}
              </button>
            </form>
          </>
        )}

        {/* ── Done ────────────────────────────────────────────────────── */}
        {state === 'done' && (
          <>
            <div
              className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full"
              style={{ background: 'rgba(34, 197, 94, 0.15)', border: '2px solid #22C55E' }}
            >
              <CheckCircle2 size={44} color="#22C55E" strokeWidth={2.2} />
            </div>
            <h1 className="text-2xl font-semibold mb-2" style={{ color: '#FFFFFF' }}>
              ¡Contraseña actualizada!
            </h1>
            <p className="text-base mb-6" style={{ color: '#B8C5E0' }}>
              Tu contraseña fue cambiada exitosamente.
            </p>
            <div
              className="flex items-center gap-3 rounded-xl p-4 text-left"
              style={{
                background: 'rgba(34, 197, 94, 0.08)',
                border: '1px solid rgba(34, 197, 94, 0.25)',
              }}
            >
              <Lock size={22} color="#22C55E" className="shrink-0" />
              <p className="text-sm" style={{ color: '#B8C5E0' }}>
                Vuelve a la aplicación <strong>ASI Security</strong> o{' '}
                <strong>ASI Patrol</strong> e inicia sesión con tu nueva
                contraseña.
              </p>
            </div>
          </>
        )}

        <p className="mt-8 text-xs" style={{ color: '#7A8AA8' }}>
          Corporación A.S.I. · Guatemala
        </p>
      </div>
    </div>
  );
}
